import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { Modal } from '@/components/shared/modal/Modal';
import { colorArray, standardIconArray } from '@/helpers/dropdownOptionHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { IconCategory, TaskType, TextColors } from '@/services/tasks/task.types';
import { useCreateTasks, useTasks } from '@/services/tasks/useTasks';
import { strings } from '@/services/translation/strings';
import { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../admin.translations';

interface AddTaskFormValues {
  name: string;
}

interface AdminAddTasksModalProps {
  open: boolean;
  onClose: () => void;
}

const AdminAddTasksModal: FunctionComponent<AdminAddTasksModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();

  const { fetching, create } = useCreateTasks();
  const methods = useForm<AddTaskFormValues>();

  const tasks = useTasks();

  const onSubmit = async (values: AddTaskFormValues) => {
    const newTask: TaskType = {
      id: '',
      name: values.name,
      displayName: `${strings.settings.manageTasks.displayName} ${tasks.length + 1}`,
      icon: { icon: standardIconArray[0].id, category: IconCategory.standard },
      textColor: TextColors.BLACK,
      color: colorArray[0].value,
      defaultDuration: 15,
      isSupplementary: true,
      isInMonthlyTotals: false,
      isInCalculatedWorkingHours: true,
      skills: [],
      optionalSkills: [],
      isActive: true
    };

    const result = await create([newTask]);

    await handleMutationResult(result, {
      successMessage: 'Tasks saved successfully.',
      errorMessage: 'Failed to save tasks.',
      customSuccessCheck: () => !!result.data?.createTasks,
      onSuccess: async () => {
        methods.reset();
        if (result.data?.createTasks) {
          const newTaskId = result.data?.createTasks?.[0].id;
          navigate(`${newTaskId}`, { state: { task: result.data.createTasks[0] } });
        }
      }
    });
  };

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} header={adminStrings.skillsSection.addDialog.title}>
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField
              label={adminStrings.tasksSection.addDialog.newTask}
              name="name"
              id="name"
              placeholder={adminStrings.tasksSection.addDialog.taskName}
              customValidatorMessage={methods.formState.errors.name?.message}
              required
              rules={{
                required: adminStrings.tasksSection.addDialog.nameRequired,
                validate: (value: string) => {
                  const isDuplicate = tasks?.some(s => s?.name === value);
                  if (isDuplicate) return adminStrings.tasksSection.addDialog.uniqueName;
                  return true;
                }
              }}
            />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={handleClose}
                loading={fetching}
                submitText={strings.common.create}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </Modal>
  );
};

export default AdminAddTasksModal;
